import { MvxFactorId, MvxFactorStates, MvxPatternId } from './MetabolicVulnerability';

/**
 * Darren Schmidt, DC's "7-Step Blueprint to Optimal Health" — the general
 * clinical framework MVX Plus tracks against. This module holds the canonical
 * 7 steps (titles + sequence) and the marker→step routing: which steps a
 * patient's MVX factor states point to, and which markers drive each.
 *
 * Step NAMES + FOCUS + routing only; the products per step live in
 * `mvxSupplements.ts` (Schmidt's own Systemic Formulas lists). The marker→step
 * routing itself is our reading of his framework — no source lays the table
 * out explicitly. See the project memory `project_mvx_7step_blueprint`.
 *
 * Pure + unit-tested; the panel is presentational.
 */

/** The four marker groups the Blueprint routes on (the three BCAAs read as one). */
export type BlueprintMarker = 'glycA' | 'sHdlP' | 'citrate' | 'bcaa';

export interface BlueprintTrigger {
  marker: BlueprintMarker;
  /** The state that indicates the step; 'either' = a miss in any direction. */
  when: 'high' | 'low' | 'either';
}

export interface BlueprintStepConfig {
  /** 1..7 — sequence matters (drainage before detox; extracellular before intra). */
  num: number;
  title: string;
  /** One-line "what you do" — client-facing, non-diagnostic. */
  focus: string;
  /** The markers this step reads, each with the direction that indicates it. */
  triggers: BlueprintTrigger[];
  /** True = every trigger must fire (step 7); otherwise any one indicates the step. */
  requireAll?: boolean;
}

/**
 * The 7 steps, in order, with their marker→step triggers. Titles are Schmidt's
 * FAIM Blueprint names. His 2026-09-20 "7 Step Remedies" slides label the same
 * steps a little differently (1 Nutrition, 2 Energy / Nourishment, 3 Drainage,
 * 4 Intestinal Cleansing, 5 Systemic & Immune Detox, 7 Intracellular Pathogens,
 * 0 Optimization); see docs/reference/mvx-schmidt-slides-2026-09-20.md.
 */
export const MVX_BLUEPRINT_STEPS: BlueprintStepConfig[] = [
  {
    num: 1,
    title: 'Foundational Foods',
    focus: 'A low-carb, meat-forward diet — remove grains and processed foods.',
    triggers: [{ marker: 'bcaa', when: 'either' }, { marker: 'sHdlP', when: 'low' }],
  },
  {
    num: 2,
    title: 'Energy',
    focus: 'Rebuild cellular energy — B1/thiamine and mitochondrial support.',
    triggers: [{ marker: 'citrate', when: 'high' }, { marker: 'bcaa', when: 'high' }],
  },
  {
    num: 3,
    title: 'Drainage',
    focus: 'Open the elimination pathways (bowel, kidney, liver, lung/skin) before detox.',
    triggers: [{ marker: 'sHdlP', when: 'low' }],
  },
  {
    num: 4,
    title: 'Intestines & Immune Cleansing',
    focus: 'Clear gut parasites and organisms.',
    triggers: [{ marker: 'glycA', when: 'high' }],
  },
  {
    num: 5,
    title: 'Full-Body Immune Cleansing',
    focus: 'Systemic organism removal with antimicrobial support.',
    triggers: [{ marker: 'glycA', when: 'high' }],
  },
  {
    num: 6,
    title: 'Powerful Detoxification',
    focus: 'Binders for metals, chemicals and biotoxins.',
    triggers: [{ marker: 'sHdlP', when: 'low' }],
  },
  {
    num: 7,
    title: 'Cellular Immune Cleansing',
    focus: 'Reach intracellular pathogens and toxins (including Lyme).',
    // The deepest step — indicated only when inflammation AND toxicity both persist.
    triggers: [{ marker: 'glycA', when: 'high' }, { marker: 'sHdlP', when: 'low' }],
    requireAll: true,
  },
];

/** One marker chip on a step box. */
export interface BlueprintMarkerChip {
  /** '▲ GlycA' / '▼ Small HDL-P' / '= BCAAs' — the symbol is the marker's ACTUAL state. */
  label: string;
  state: 'high' | 'low' | 'opt';
  /** True when that state is the one that indicates this step. */
  driver: boolean;
}

export interface BlueprintStepResult {
  num: number;
  title: string;
  focus: string;
  indicated: boolean;
  /** Labels of the chips that lit the step; empty when it isn't indicated. */
  drivers: string[];
  /**
   * Every marker the step reads that has a value, off-target ones first, then
   * the optimal ('=') ones — so a box always shows what would light it, and a
   * dimmed box shows why it isn't lit. Markers without a value are left out.
   */
  markers: BlueprintMarkerChip[];
}

const BCAA: MvxFactorId[] = ['valine', 'leucine', 'isoleucine'];
const LABEL: Record<BlueprintMarker, string> = { glycA: 'GlycA', sHdlP: 'Small HDL-P', citrate: 'Citrate', bcaa: 'BCAAs' };
const SYMBOL = { high: '▲', low: '▼', opt: '=' } as const;

/**
 * The states a marker group is in. A single factor gives at most one; the BCAA
 * trio can be high AND low at once (say valine high, leucine low) and then
 * gives both, and reads optimal only when every measured one is.
 */
function markerStates(s: MvxFactorStates, marker: BlueprintMarker): ('high' | 'low' | 'opt')[] {
  if (marker !== 'bcaa') {
    const state = s[marker];
    return state === 'absent' ? [] : [state];
  }
  const measured = BCAA.map(k => s[k]).filter(st => st !== 'absent');
  if (measured.length === 0) return [];
  const out: ('high' | 'low' | 'opt')[] = [];
  if (measured.includes('high')) out.push('high');
  if (measured.includes('low')) out.push('low');
  return out.length > 0 ? out : ['opt'];
}

/** Resolve factor states to the 7 steps: which are indicated, their chips and drivers. */
export function resolveBlueprintSteps(s: MvxFactorStates): BlueprintStepResult[] {
  return MVX_BLUEPRINT_STEPS.map(step => {
    const off: BlueprintMarkerChip[] = [];
    const optimal: BlueprintMarkerChip[] = [];
    let fired = 0;
    step.triggers.forEach(t => {
      let hit = false;
      markerStates(s, t.marker).forEach(state => {
        const driver = state !== 'opt' && (t.when === 'either' || t.when === state);
        if (driver) hit = true;
        (state === 'opt' ? optimal : off).push({ label: `${SYMBOL[state]} ${LABEL[t.marker]}`, state, driver });
      });
      if (hit) fired++;
    });
    const indicated = step.requireAll ? fired === step.triggers.length : fired > 0;
    const markers = [...off, ...optimal];
    const drivers = indicated ? markers.filter(m => m.driver).map(m => m.label) : [];
    return { num: step.num, title: step.title, focus: step.focus, indicated, drivers, markers };
  });
}

/**
 * Which Blueprint steps each MVX pattern points to — for the "→ Step N" chips on
 * the pattern boxes. Optimal has no steps. (IVX story → immune/detox steps; MMX
 * story → diet/energy steps.)
 */
export const PATTERN_STEPS: Partial<Record<MvxPatternId, number[]>> = {
  [MvxPatternId.Inflammation]: [4, 5, 7],
  [MvxPatternId.ToxicBurden]: [3, 6, 7],
  [MvxPatternId.Mitochondrial]: [2],
  [MvxPatternId.CarbLoad]: [1, 2],
  [MvxPatternId.Malnutrition]: [1],
};
